import {
	MenuItem,
	MenuItemProps,
	MenuList,
	MenuProps,
	PaperProps,
	Popover,
	PopoverOrigin,
	PopoverProps,
} from "@mui/material";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormControlTooltip, FormControlTooltipProps } from "../tooltip";
import styles from "./index.module.css";

export type CoreMenuPopoverProps = Omit<PopoverProps, "ref">;

export type CoreMenuProps = CoreMenuPopoverProps & {
	slotProps?: CoreMenuPopoverProps["slotProps"] & {
		menuList?: Omit<Exclude<MenuProps["MenuListProps"], undefined>, "aria-labelledby">;
	};
};

export type CoreMenuItemProps = MenuItemProps &
	Pick<FormControlTooltipProps<false>, "tooltip" | "disabledTooltip" | "slotProps">;

const ARROW_RADIUS = 8;
const ARROW_OFFSET_VERTICAL = 5;
const ARROW_OFFSET_HORIZONTAL = 15;

export function CoreMenuPopover({
	anchorOrigin: specifiedAnchorOrigin,
	transformOrigin: specifiedTransformOrigin,
	slotProps,
	children,
	...popoverProps
}: CoreMenuPopoverProps) {
	const { anchorEl } = popoverProps;

	const paperRef = useRef<HTMLDivElement | undefined>(undefined);
	const [paperRect, setPaperRect] = useState<DOMRect | undefined>();
	const resizeObserverRef = useRef<ResizeObserver>(
		new ResizeObserver(() => {
			setPaperRect(paperRef.current?.getBoundingClientRect());
		}),
	);
	const popoverElementCallback = useCallback((elem: HTMLDivElement) => {
		if (elem ?? undefined !== paperRef.current) {
			if (paperRef.current) {
				resizeObserverRef.current?.unobserve(paperRef.current);
			}
			paperRef.current = elem ?? undefined;
			if (elem) {
				resizeObserverRef.current?.observe(elem);
			}
		}
	}, []);

	const [anchorOrigin, transformOrigin] = useMemo((): [PopoverOrigin, PopoverOrigin] => {
		let autoFlip = false;
		const anchorElement = typeof anchorEl === "function" ? anchorEl() : anchorEl;
		if (!specifiedAnchorOrigin && !specifiedTransformOrigin && anchorElement && paperRect && window.visualViewport) {
			const anchorGlobalPosition = anchorElement.getBoundingClientRect();
			const menuHeight = paperRect.height + ARROW_OFFSET_VERTICAL + ARROW_RADIUS;
			const anchorTopSpace = anchorGlobalPosition.top - window.visualViewport.offsetTop;
			const anchorBottomSpace =
				window.visualViewport.offsetTop + window.visualViewport.height - anchorGlobalPosition.bottom;
			autoFlip = anchorBottomSpace <= menuHeight && anchorTopSpace > anchorBottomSpace;
		}
		return [
			specifiedAnchorOrigin ?? {
				vertical: autoFlip ? "top" : "bottom",
				horizontal: "center",
			},
			specifiedTransformOrigin ?? {
				vertical: autoFlip ? "bottom" : "top",
				horizontal: "right",
			},
		];
	}, [paperRect, anchorEl, specifiedAnchorOrigin, specifiedTransformOrigin]);

	let verticalTransform = 0;
	if (transformOrigin.vertical === "top" || transformOrigin.vertical === "bottom") {
		verticalTransform = ARROW_OFFSET_VERTICAL + ARROW_RADIUS;
		if (transformOrigin.vertical === "bottom") {
			verticalTransform *= -1;
		}
	}

	let horizontalTransform = 0;
	if (transformOrigin.horizontal === "left" || transformOrigin.horizontal === "right") {
		horizontalTransform = ARROW_OFFSET_HORIZONTAL + ARROW_RADIUS * 2;
		if (transformOrigin.horizontal === "left") {
			horizontalTransform *= -1;
		}
	}

	const arrowOffset = useMemo(() => {
		if (!paperRef.current || !paperRect) return undefined;

		const anchorElement = typeof anchorEl === "function" ? anchorEl() : anchorEl;
		if (!anchorElement) return undefined;

		const anchorRect = anchorElement.getBoundingClientRect();

		const arrowX = (anchorRect.left + anchorRect.right) / 2;

		if (arrowX < paperRect.left + ARROW_RADIUS || arrowX > paperRect.right - ARROW_RADIUS) {
			return undefined;
		}

		return arrowX - ARROW_RADIUS - paperRect.left;
	}, [paperRect, anchorEl]);

	const generatePaperProps = (specifiedProps?: Partial<PaperProps>): Partial<PaperProps> => {
		return {
			className: styles.displayedPaper,
			sx: {
				...specifiedProps?.sx,
				"&::before": {
					visibility: arrowOffset == null ? "hidden" : "visible",
					left: arrowOffset,
					...(transformOrigin.vertical === "bottom"
						? {
								// Menu opening from top
								top: "calc(100% - 11px)",
								transform: "rotate(225deg)",
							}
						: {
								// Menu opening from bottom
								bottom: "calc(100% - 11px)",
								transform: "rotate(45deg)",
							}),
				},
			},
			style: {
				transform: `translate(${horizontalTransform}px, ${verticalTransform}px)`,
			},
			ref: popoverElementCallback,
		};
	};

	const specifiedObjectPaperProps = typeof slotProps?.paper === "function" ? undefined : slotProps?.paper;

	return (
		<Popover
			{...popoverProps}
			anchorOrigin={anchorOrigin}
			transformOrigin={transformOrigin}
			// only deprecated PaperProps is working for some reason
			PaperProps={{
				...specifiedObjectPaperProps,
				...generatePaperProps(specifiedObjectPaperProps),
			}}
			marginThreshold={null}
		>
			{children}
		</Popover>
	);
}

export default function CoreMenu({
	"aria-labelledby": labelledBy,
	slotProps,
	children,
	...popoverProps
}: CoreMenuProps) {
	const [inShadowDom, setInShadowDom] = useState<boolean>(false);

	// Using activeIndex instead of querying at runtime because MUI does
	// weird things in Shadow dom and doesn't give correct element that is active
	const [activeIndex, setActiveIndex] = useState<number>(0);
	const menuListRef = useRef<HTMLUListElement | null>(null);

	const focusableMenuItemsSelector = "[role='menuitem']:not([disabled]):not([aria-disabled='true'])";

	const setMenuListRef = useCallback((node: HTMLUListElement | null) => {
		if (node) {
			menuListRef.current = node;
			setInShadowDom(node.getRootNode() instanceof ShadowRoot);
		}
	}, []);

	// On menu open, first menu item doesn't get focused, ONLY in shadow dom
	useEffect(() => {
		if (inShadowDom && menuListRef.current) {
			const items: HTMLElement[] = Array.from(
				menuListRef.current.querySelectorAll<HTMLElement>(focusableMenuItemsSelector) ?? [],
			);

			if (items.length > 0) {
				items[0].focus();
				setActiveIndex(0);
			}
		}
	}, [inShadowDom]);

	return (
		<CoreMenuPopover {...popoverProps}>
			<MenuList
				// autoFocusItem does not work in shadow dom
				autoFocusItem={!inShadowDom}
				{...slotProps?.menuList}
				className={`${styles.menuList} ${slotProps?.menuList?.className || ""}`}
				aria-labelledby={labelledBy}
				ref={setMenuListRef}
				onKeyDown={(event: React.KeyboardEvent<HTMLUListElement>) => {
					// Patch arrow keys not working in Shadow dom
					if ((event.key === "ArrowDown" || event.key === "ArrowUp") && inShadowDom) {
						event.preventDefault();
						event.stopPropagation();

						const items: HTMLElement[] = Array.from(
							menuListRef.current?.querySelectorAll<HTMLElement>(focusableMenuItemsSelector) ?? [],
						);

						if (!items.length) return;
						const goingDown = event.key === "ArrowDown";
						const nextIndex =
							activeIndex === -1
								? goingDown
									? 0
									: items.length - 1
								: goingDown
									? (activeIndex + 1) % items.length
									: (activeIndex - 1 + items.length) % items.length;

						setActiveIndex(nextIndex);
						items[nextIndex]?.focus();
					}
					// Mui doesn't support Tab closing the menu
					else if (event.key === "Escape" || event.key === "Tab") {
						event.preventDefault();
						const anchor = popoverProps.anchorEl as HTMLElement | null;
						popoverProps.onClose?.(event, "escapeKeyDown");

						// Focus doesn't return to menu button on close, ONLY in shadow dom
						if (anchor && inShadowDom) {
							// Delay to let Popover unmount cleanly
							setTimeout(() => anchor.focus(), 0);
						}
					}
				}}
			>
				{children}
			</MenuList>
		</CoreMenuPopover>
	);
}

export const CoreMenuItem = forwardRef<HTMLLIElement, CoreMenuItemProps>(function ArrowMenuItem(
	{ onClick, disabled, tooltip, disabledTooltip, className, children, slotProps, ...menuItemProps },
	ref,
) {
	return (
		<FormControlTooltip
			tooltip={tooltip}
			disabledTooltip={disabledTooltip}
			wrapDisabledChild={false}
			slotProps={{ ...slotProps, tooltip: { describeChild: true, ...slotProps?.tooltip } }}
		>
			<MenuItem
				{...menuItemProps}
				disabled={disabled}
				onClick={disabled ? undefined : onClick}
				className={`${styles.menuItem} ${className || ""}`}
				ref={ref}
			>
				{children}
			</MenuItem>
		</FormControlTooltip>
	);
});
